import { SEARCH_SUCCESS, type searchSuccessAction, selectProductGroup } from "../actions";
import { type ActionType, type AnyAction } from "../../../redux/types";
import { type SRPSeriesTreeNode } from "../../models";
import { type ThunkAction } from "redux-thunk";
import { type RootState } from "../../../redux/store";
import { getSearchPageType, isPlpForSeries, SearchPageType } from "~/Search/useSearchPageType";

export interface SeriesTreeState {
  tree: SRPSeriesTreeNode[];
  parentCategoryCode: string | null;
  isSeriesSelected: boolean;
}

export const seriesTreeInitialState: SeriesTreeState = {
  tree: [],
  parentCategoryCode: null,
  isSeriesSelected: getSearchPageType() === SearchPageType.PLP ? isPlpForSeries() : false,
};

export const TOGGLE_SERIES_TREE_NODE = "srp: toggle series tree node";
export const SELECT_SERIES_TREE_NODE = "srp: select series tree node";
export const SERIES_TREE_BACK = "srp: go back from series tree selection";

type ThunkResult<R> = ThunkAction<R, RootState, undefined, AnyAction>;

export function setCategoryFilterFromSeriesTree(node: SRPSeriesTreeNode): ThunkResult<void> {
  return async (dispatch, getState) => {
    dispatch(selectSeriesTreeCategoryAction(node));
    dispatch(selectProductGroup(node.categoryCode));
  };
}

export function selectSeriesTreeCategoryAction(node: SRPSeriesTreeNode) {
  return {
    type: SELECT_SERIES_TREE_NODE,
    payload: {
      node,
    },
  } as const;
}

export function toggleSeriesTreeNodeAction(nodeId: string, isExpanded: boolean) {
  return {
    type: TOGGLE_SERIES_TREE_NODE,
    payload: {
      nodeId,
      isExpanded,
    },
  } as const;
}

export function pressSeriesTreeBack(): ThunkResult<void> {
  return async (dispatch, getState) => {
    const previousCategoryCode = getState().srp.seriesTree.parentCategoryCode;
    if (!previousCategoryCode) {
      return;
    }
    dispatch(pressSeriesTreeBackAction());
    dispatch(selectProductGroup(previousCategoryCode));
  };
}

export function pressSeriesTreeBackAction() {
  return {
    type: SERIES_TREE_BACK,
    payload: {},
  } as const;
}

type SeriesTreeActions =
  | ReturnType<typeof toggleSeriesTreeNodeAction>
  | ReturnType<typeof searchSuccessAction>
  | ReturnType<typeof selectSeriesTreeCategoryAction>
  | ReturnType<typeof pressSeriesTreeBackAction>;

export const seriesTreeReducer = (
  state: SeriesTreeState = seriesTreeInitialState,
  srpAction: ActionType<{}>,
): SeriesTreeState => {
  const action = srpAction as SeriesTreeActions;

  switch (action.type) {
    case SEARCH_SUCCESS: {
      const { data } = action.payload;
      return {
        ...state,
        tree: data.seriesTree ?? [],
      };
    }

    case TOGGLE_SERIES_TREE_NODE: {
      if (!state || !state.tree) {
        return state;
      }
      let newTree = state.tree.map((node) =>
        updateNodeInTree(node, { id: action.payload.nodeId, expanded: action.payload.isExpanded }),
      );
      newTree = arraysEqual(state.tree, newTree) ? state.tree : newTree;
      return {
        ...state,
        tree: newTree,
      };
    }

    case SELECT_SERIES_TREE_NODE: {
      const { node } = action.payload;
      return {
        ...state,
        parentCategoryCode: node.parentCategoryCode,
        isSeriesSelected: node.isSeries,
      };
    }

    case SERIES_TREE_BACK: {
      return {
        ...state,
        parentCategoryCode: null,
        isSeriesSelected: false,
      };
    }

    default: {
      return state;
    }
  }
};

export function updateNodeInTree<T>(
  root: SRPSeriesTreeNode,
  update: Omit<Partial<SRPSeriesTreeNode>, "children"> & Pick<SRPSeriesTreeNode, "id">,
): SRPSeriesTreeNode {
  if (root.id === update.id) {
    return {
      ...root,
      ...update,
    };
  } else {
    if (root.children.length === 0) {
      return root;
    }
    const children = root.children.map((child) => updateNodeInTree(child, update));
    if (arraysEqual(children, root.children)) {
      return root;
    } else {
      return {
        ...root,
        children,
      };
    }
  }
}

export function arraysEqual(a: any[], b: any[]) {
  if (a === b) {
    return true;
  }
  if (a == null || b == null) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }

  // If you don't care about the order of the elements inside
  // the array, you should sort both arrays here.
  // Please note that calling sort on an array will modify that array.
  // you might want to clone your array first.

  for (let i = 0; i < a.length; ++i) {
    if (a[i] !== b[i]) {
      return false;
    }
  }
  return true;
}
